/**
 * Centralized cleanup of frontend game-session state.
 *
 * One helper, two modes — used everywhere the player intentionally returns
 * to the lobby OR a terminal WS close fires. Replaces the ~20 ad-hoc
 * `localStorage.removeItem(...)` clusters we used to maintain by hand.
 *
 * Both modes ALWAYS set the `session-just-ended` sessionStorage token.
 * `ProtectedRoute` reads it (30s TTL) to suppress its auto-redirect-to-game,
 * which would otherwise bounce the player right back if Firestore still
 * reports an active session for ~1s after end.
 *
 * Per Inv 5 ("local disconnect is not session end"), this helper does NOT
 * end the backend session. It only clears local routing state.
 *
 * NOTE: GameConnectionManager identity reset (`lastUrl`/`lastSessionId`) is
 * intentionally NOT done here. The helper has no GCM reference; close
 * handlers inside GCM call `this.clearIdentity()` themselves so each
 * instance resets its own identity (the singleton in production, fresh
 * mocks in tests).
 */
import { useGameStore } from './stores/gameStore';

export type CleanupMode =
  /**
   * Suppress auto-redirect to `/play/<id>` and forget the WS URL, but leave
   * heavier per-session caches alone. Use when the player is voluntarily
   * leaving the game (idle timeout, stuck-state Return-to-lobby, dead
   * pointer in ProtectedRoute) — the backend session may still be alive.
   */
  | 'route-hints'
  /**
   * Full reset: route-hints + every game-related localStorage key + game
   * store reset. Use on terminal WS close codes where the session is over
   * and the next adventure should start cold. Identity reset is done inline
   * by the GCM call site (see `clearIdentity`).
   */
  | 'all';

/** localStorage keys that route ProtectedRoute back to /play/<id>. */
const ROUTE_HINT_KEYS = [
  'active-game-session',
  'game-session-ws-url',
  'game-session-party',
  'game-last-session-id',
  // `game-session-duel-portraits` is a per-navigation hint — it tells the
  // ReconnectScreen which two faces to render during the lobby→duel transition.
  // Letting it survive past `route-hints` cleanup means a stale opponent's
  // portrait can attach itself to the next duel's loader (charId mismatches
  // the new opponent but the localStorage merge in LobbyLayout still pulled
  // the prior imageUrl as a fallback). Cleared with the rest of the route
  // state so each duel's loader starts from server-authoritative data.
  'game-session-duel-portraits',
] as const;

/**
 * Other game-related localStorage keys cleared on a full reset. Excludes
 * `game-session-last-summary` — that one persists across sessions for the
 * "Previously..." recap on the next adventure.
 */
const FULL_RESET_KEYS = [
  'game-session-hero-video',
  'game-session-hero-image',
  'game-session-module',
  'game-storage',
] as const;

export function clearGameSessionState(mode: CleanupMode): void {
  for (const key of ROUTE_HINT_KEYS) {
    localStorage.removeItem(key);
  }

  // ProtectedRoute reads this within a 30s TTL to suppress auto-redirect.
  sessionStorage.setItem('session-just-ended', Date.now().toString());

  if (mode === 'all') {
    for (const key of FULL_RESET_KEYS) {
      localStorage.removeItem(key);
    }
    useGameStore.getState().reset();
  }
}
